import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { cn, MMSCAN_UI_I18N } from '@mmscan/angular-ui/core';
import { cva, type VariantProps } from 'class-variance-authority';

export const spinnerVariants = cva('animate-spin rounded-full border-current border-r-transparent', {
  variants: {
    size: {
      sm: 'size-4 border-2',
      md: 'size-8 border-[3px]',
      lg: 'size-12 border-4',
    },
  },
  defaultVariants: { size: 'md' },
});

export type SpinnerSize = NonNullable<VariantProps<typeof spinnerVariants>['size']>;

/** Loading indicator announced to screen readers via `role="status"`. */
@Component({
  selector: 'mmscan-spinner',
  template: `
    <span [class]="spinnerClasses()" aria-hidden="true"></span>
    <span class="sr-only">{{ label() ?? i18n().loading }}</span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'status',
    '[class]': 'hostClasses()',
  },
})
export class MmscanSpinner {
  protected readonly i18n = inject(MMSCAN_UI_I18N);

  readonly size = input<SpinnerSize>('md');
  /** Screen-reader text; defaults to the translated "Loading". */
  readonly label = input<string>();
  readonly class = input<string>('');

  protected readonly spinnerClasses = computed(() => spinnerVariants({ size: this.size() }));
  protected readonly hostClasses = computed(() =>
    cn('inline-flex items-center justify-center text-primary-text', this.class())
  );
}
